import { Loader2, Inbox } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const PageHeader = ({ overline, title, subtitle, children, testid }) => (
  <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between pb-8 mb-8 border-b border-[#29292D] lf-fade">
    <div className="max-w-2xl">
      {overline && <div className="lf-overline mb-3">{overline}</div>}
      <h1 data-testid={testid} className="text-4xl sm:text-5xl font-medium tracking-tight leading-[1.05] text-[#F2F0EC]">{title}</h1>
      {subtitle && <p className="mt-3 text-sm sm:text-base text-[#929197] leading-relaxed">{subtitle}</p>}
    </div>
    {children && <div className="flex flex-wrap gap-2">{children}</div>}
  </div>
);

export const Card = ({ className = "", children, ...p }) => (
  <div className={`lf-card p-5 sm:p-6 ${className}`} {...p}>{children}</div>
);

export const SectionTitle = ({ children, action }) => (
  <div className="flex items-center justify-between mb-4">
    <h2 className="text-2xl font-medium text-[#F2F0EC]">{children}</h2>
    {action}
  </div>
);

export const EmptyState = ({ title, text, children, testid }) => (
  <div data-testid={testid} className="border border-dashed border-[#29292D] rounded-md p-8 text-center">
    <Inbox className="mx-auto mb-3 text-[#545B6B]" size={26} strokeWidth={1.4} />
    <div className="font-serif text-xl text-[#F2F0EC]">{title}</div>
    {text && <p className="text-sm text-[#929197] mt-1 max-w-md mx-auto leading-relaxed">{text}</p>}
    {children && <div className="mt-4 flex justify-center gap-2">{children}</div>}
  </div>
);

export const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="block text-xs font-semibold tracking-wide text-[#929197] mb-1.5">{label}</span>
    {children}
    {hint && <span className="block text-xs text-[#6b6a70] mt-1">{hint}</span>}
  </label>
);

export const Tag = ({ children, tone = "default", ...p }) => {
  const tones = {
    default: "border-[#29292D] text-[#929197]",
    ivory: "border-[#E6E2D6]/40 text-[#E6E2D6]",
    blue: "border-[#545B6B] text-[#B6BCCB]",
    warn: "border-[#7a5c2e] text-[#d4a95f]",
    red: "border-[#7a3434] text-[#d98a8a]",
  };
  return <span className={`inline-flex items-center border px-2 py-0.5 text-[11px] rounded-sm ${tones[tone]}`} {...p}>{children}</span>;
};

export const Spinner = ({ label }) => (
  <span className="inline-flex items-center gap-2"><Loader2 className="animate-spin" size={16} />{label}</span>
);

export const ErrorBox = ({ error, testid = "error-box" }) =>
  error ? (
    <div data-testid={testid} role="alert" className="border border-[#7a3434] bg-[#1e1214] text-[#e3a6a6] text-sm rounded-sm px-4 py-3 leading-relaxed">
      {error.message || String(error)}
    </div>
  ) : null;

export const DemoBadge = ({ children = "DEMONSTRASI — bukan transaksi nyata" }) => (
  <span data-testid="demo-badge" className="inline-flex items-center border border-[#7a5c2e] text-[#d4a95f] text-[11px] tracking-wide px-2 py-0.5 rounded-sm">{children}</span>
);

export const ConfirmDialog = ({ open, onOpenChange, title, description, confirmLabel = "Hapus", onConfirm, testid = "confirm-dialog" }) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent data-testid={testid} className="bg-[#1B1B1F] border-[#29292D] text-[#F2F0EC]">
      <AlertDialogHeader>
        <AlertDialogTitle className="font-serif text-2xl font-medium">{title}</AlertDialogTitle>
        <AlertDialogDescription className="text-[#929197]">{description}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid={`${testid}-cancel`} className="bg-transparent border-[#29292D] text-[#F2F0EC] hover:bg-[#151518] hover:text-[#F2F0EC]">Batal</AlertDialogCancel>
        <AlertDialogAction data-testid={`${testid}-confirm`} onClick={onConfirm} className="bg-[#E6E2D6] text-[#0B0B0D] hover:bg-[#E6E2D6]/90">{confirmLabel}</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const BookCover = ({ book, size = "md" }) => {
  const dims = size === "lg" ? "w-full aspect-[2/3]" : size === "sm" ? "w-14 h-20" : "w-full aspect-[2/3]";
  return (
    <div className={`${dims} relative overflow-hidden border border-[#29292D] bg-[#151518] shrink-0`} data-testid="book-cover">
      {book.texture && <img src={book.texture} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" />}
      <div className="absolute inset-0 bg-black/55" />
      <div className="relative h-full flex flex-col justify-between p-2.5 sm:p-4">
        {size !== "sm" && <div className="text-[9px] tracking-[0.2em] uppercase text-[#E6E2D6]/80">{book.category || "Buku"}</div>}
        <div>
          <div className={`font-serif text-[#F2F0EC] leading-tight ${size === "sm" ? "text-[10px]" : "text-xl sm:text-2xl"}`}>{book.title}</div>
          {size !== "sm" && <div className="text-[11px] text-[#E6E2D6]/80 mt-1">{book.author}</div>}
        </div>
        {size !== "sm" && <div className="text-[9px] uppercase tracking-widest text-[#d4a95f]">Sampul placeholder</div>}
      </div>
    </div>
  );
};

export const Logo = ({ size = 36 }) => (
  <img src="/images/logo.png" alt="Logika Filsuf" width={size} height={size} style={{ width: size, height: size }} className="rounded-full shrink-0 object-cover" data-testid="brand-logo" />
);
